import API from "serviceshift-ui/api-client";

const BASE_URL = "/admin/v4/custom_form_templates";

const submissionsUrl = (jobId) => `/v2/jobs/${jobId}/custom_form_submissions`;

// Single data layer for custom forms: the admin/v4 template endpoints behind
// the library and the builder, plus the job-nested v2 submission endpoints
// behind the job page's Forms tab. Actions return the parsed payload so callers
// can adopt the server's tree (with freshly assigned ids) as source of truth
// after each save. Errors reject and are flashed by the global interceptor.
export default {
  fetchCustomFormTemplate({ commit }, id) {
    return API.makeRequest(`${BASE_URL}/${id}`, { method: "GET" }).then(
      (res) => {
        commit("replace", {
          state: "currentCustomFormTemplate",
          data: res.data
        });
        return res.data;
      }
    );
  },
  createCustomFormTemplate(_, template) {
    // ignoreError: the builder owns save-error display (it maps the 422 errors
    // hash to inline field messages), so we suppress the interceptor's generic
    // toast that would otherwise read "unexpected error" for a validation fail.
    return API.makeRequest(BASE_URL, {
      method: "POST",
      data: { custom_form_template: template },
      headers: { ignoreError: true }
    }).then((res) => res.data);
  },
  updateCustomFormTemplate(_, { id, template }) {
    return API.makeRequest(`${BASE_URL}/${id}`, {
      method: "PUT",
      data: { custom_form_template: template },
      headers: { ignoreError: true }
    }).then((res) => res.data);
  },
  publishCustomFormTemplate(_, id) {
    return API.makeRequest(`${BASE_URL}/${id}/publish`, {
      method: "PUT"
    }).then((res) => res.data);
  },
  archiveCustomFormTemplate(_, id) {
    return API.makeRequest(`${BASE_URL}/${id}/archive`, {
      method: "PUT"
    }).then((res) => res.data);
  },
  unarchiveCustomFormTemplate(_, id) {
    return API.makeRequest(`${BASE_URL}/${id}/unarchive`, {
      method: "PUT"
    }).then((res) => res.data);
  },
  duplicateCustomFormTemplate(_, id) {
    return API.makeRequest(`${BASE_URL}/${id}/duplicate`, {
      method: "POST"
    }).then((res) => res.data);
  },
  destroyCustomFormTemplate(_, id) {
    return API.makeRequest(`${BASE_URL}/${id}`, { method: "DELETE" });
  },

  // Published, unarchived templates for the attach picker. Flat list — no
  // sections tree — so it is not interchangeable with fetchCustomFormTemplate.
  fetchAttachableCustomFormTemplates() {
    return API.makeRequest(BASE_URL, { method: "GET" }).then(
      (res) => res.data.custom_form_templates
    );
  },

  // Source URL and filter definitions for the customer record's Forms tab. Fetched
  // on tab mount rather than handed down with the customer page: two of the
  // filters build their options from tenant-wide queries, and the tab is lazily
  // mounted, so the profile must not prepay for a tab most views never open.
  fetchCustomerCustomFormPageData(_, customerId) {
    return API.makeRequest(
      `/customers/${customerId}/custom_form_submissions/initial_page_data`,
      { method: "GET" }
    ).then((res) => res.data);
  },

  // The list is the lightweight shape: status and progress, no sections tree.
  //
  // include_archived is off on the server by default, which is what the tech app
  // wants. The job page passes it and filters locally instead — the attach picker
  // builds its exclude list from this payload, so a server-filtered list would
  // offer a template that is archived on the job and then 409 on selection.
  fetchJobCustomFormSubmissions(_, { jobId, includeArchived = false }) {
    return API.makeRequest(submissionsUrl(jobId), {
      method: "GET",
      params: { include_archived: includeArchived }
    }).then((res) => res.data.custom_form_submissions);
  },

  // A single submission carries the full sections -> questions -> answer tree.
  fetchJobCustomFormSubmission(_, { jobId, id }) {
    return API.makeRequest(`${submissionsUrl(jobId)}/${id}`, {
      method: "GET"
    }).then((res) => res.data);
  },

  // Save-as-you-go for a single answer. POST upserts by question; PATCH targets an
  // existing answer by id. Either way the response is { answer, progress }, so the
  // caller reconciles the row and the completion gate from one round-trip.
  //
  // lock_version is mandatory for an existing answer — the server rejects a write
  // without it rather than allowing a last-write-wins — and a stale one comes back
  // 409. Callers must surface that, never retry blindly.
  writeCustomFormAnswer(_, { jobId, submissionId, answerId, attributes }) {
    const url = `${submissionsUrl(jobId)}/${submissionId}/answers`;
    return API.makeRequest(answerId ? `${url}/${answerId}` : url, {
      method: answerId ? "PATCH" : "POST",
      data: { answer: attributes }
    }).then((res) => res.data);
  },

  // Resolves with the created submission's full tree, so the caller can open it
  // without a second fetch.
  attachCustomFormToJob(_, { jobId, customFormTemplateId, appointmentId }) {
    return API.makeRequest(submissionsUrl(jobId), {
      method: "POST",
      data: {
        custom_form_submission: {
          custom_form_template_id: customFormTemplateId,
          appointment_id: appointmentId
        }
      }
    }).then((res) => res.data);
  },
  // Finalizing is normally the tech app's move. The admin needs it for the case
  // where every required answer is in but the submit never happened, which the
  // close-out gate keeps blocking with nothing for the office to act on.
  submitJobCustomFormSubmission(_, { jobId, id }) {
    return API.makeRequest(`${submissionsUrl(jobId)}/${id}/submit`, {
      method: "PUT"
    }).then((res) => res.data);
  },

  // Archiving keeps the answers and the audit trail; it takes the form out of the
  // close-out gate and out of the job's active list. Both endpoints are idempotent,
  // so a repeat call resolves with the unchanged submission rather than rejecting.
  archiveJobCustomFormSubmission(_, { jobId, id }) {
    return API.makeRequest(`${submissionsUrl(jobId)}/${id}/archive`, {
      method: "PUT"
    }).then((res) => res.data);
  },
  unarchiveJobCustomFormSubmission(_, { jobId, id }) {
    return API.makeRequest(`${submissionsUrl(jobId)}/${id}/unarchive`, {
      method: "PUT"
    }).then((res) => res.data);
  },

  // The addresses the job already holds, for the send modal to offer. Same
  // endpoint the quote and invoice email modals read, so the office sees one set
  // of recipients whichever document they are sending.
  //
  // Filtered to `checked`, and taken from BOTH lists rather than `required`
  // alone. The payload's required/optional split is not the split the send
  // endpoint enforces: it partitions on an `optional` flag that marks the
  // address's own emails optional even though Address#required_emails — the set
  // the endpoint matches a recipient list against — contains them. `checked` is
  // what that set corresponds to exactly, on both sides of the partition, so
  // filtering on it offers the office every address the send would accept and no
  // address it would refuse.
  fetchCustomFormEmailRecipients(_, { customerId, addressId }) {
    return API.makeRequest(
      `/v2/customers/${customerId}/addresses/${addressId}/contact`
    ).then((res) => {
      const emails = res.data.emails ?? {};
      return [...(emails.required ?? []), ...(emails.optional ?? [])].filter(
        (email) => email.checked
      );
    });
  },

  // `additional_emails` is the whole recipient list despite its name — the server
  // uses the job's own addresses only when it arrives empty, so a partial list
  // here would silently drop the recipients the sender left checked.
  //
  // Resolves with the list the server accepted, which is what the toast names.
  emailJobCustomFormSubmission(_, { jobId, id, emails }) {
    return API.makeRequest(`${submissionsUrl(jobId)}/${id}/email`, {
      method: "POST",
      data: { additional_emails: emails }
    }).then((res) => res.data.emails);
  }
};
